import { SupplyListRequestParams, getSupplyList } from "@/api/supply";
import { useLocationInfo, useReportPageLoaded,useSupplyData } from "@/context/PageStateContext";
import useLogin from "@/hooks/useLogin";
import { getCommonParams } from "@/lib/commonParams";
import { isServer } from "@/lib/constant";
import { getMtAppVersion, getMtWXAppVersion } from "@/lib/getBrowserEnv";
import { Level, reportCustomMetric, reportError, reportPerfMetric } from "@/lib/owl";
import { addCouponInfo, formatPoiInfo } from "@/lib/supplyUtils/formatPoiInfo";
import { formatProductInfo } from "@/lib/supplyUtils/formatProductInfo";
import { CouponSource } from "@/types/poiCard";
import {
  EnhancedGroupBuySupplyCard,
  EnhancedSupplyCard,
  EnhancedWayneSupplyCard,
  SupplyCard,
  SupplyType,
} from "@/types/supplyList";
import { CouponSelection, QuickSelection, Tab } from "@/types/tab";
import { useCallback, useRef, useState } from "react";

// 外卖供给列表缓存
interface SupplyListCacheItem {
  supplyList: EnhancedSupplyCard[];
  isLastPage: boolean;
  timestamp: number;
  requestId: string;
  offset: number;
}

type GetCacheSupplyListResult = {
  isCacheValid: boolean;
  supplyList: EnhancedSupplyCard[];
  isLastPage: boolean;
  requestId: string | null;
  offset: number;
};

const NO_SUPPLY_LIST_CACHE: GetCacheSupplyListResult = {
  isCacheValid: false,
  supplyList: [],
  isLastPage: false,
  requestId: null,
  offset: 0,
};

// 分页大小
const PAGE_SIZE = 10;

interface RequestOptions {
  requestId: string | null;
  offset: number;
}

type GetSupplyListSuccessRateTags = {
  requestScene: 'init' | 'loadMore';
}
// 上报获取供给列表成功率
const reportGetSupplyListSuccessRate = (isSuccess: boolean, requestScene: GetSupplyListSuccessRateTags['requestScene']) => {
  reportCustomMetric({
    key: "getSupplyList",
    value: isSuccess ? 1 : 0,
    tags: {
      requestScene,
    }
  })
}

const reportGetSupplyListStart = (value: number) => {
  // 上报请求供给列表数据开始时间
  if ((window as any)?.__PAGE_JS_START__) {
    reportPerfMetric({
      key: "getSupplyListDataStart",
      value,
    })
  }
}

const reportGetSupplyListEnd = (value: number) => {
  // 上报请求供给列表数据耗时
  if (!isServer) {
    reportPerfMetric({
      key: "getSupplyListDataCost",
      value,
    })
  }
}

const reportGetSupplyListCost = (value: number) => {
  // 上报请求供给列表数据结束时间
  if ((window as any)?.__PAGE_JS_START__) {
    reportPerfMetric({
      key: "getSupplyListDataEnd",
      value,
    })
  }
}

const useSupplyList = function (props: {
  activeTab: Tab | null;
  activeQuickSelection: QuickSelection | null;
  activeCouponSelection: CouponSelection | null;
  // 默认 bizScene：由于页面加载时 Tab 数据和供给列表会并行请求，首次请求供给列表获取不到 bizScene，会使用 defaultBizScene 请求
  // 目前分销中心页、外卖商家出佣会场页的 defaultBizScene 对应的都是外卖数据
  // 因此下面也会使用 defaultBizScene 来判断是否是请求外卖供给
  defaultBizScene: number;
}) {
  const { setModuleLoaded } = useReportPageLoaded();
  // 选中的 Tab、普通快筛、券快筛
  const { activeTab, activeQuickSelection, activeCouponSelection, defaultBizScene } = props;
  const { login } = useLogin();
  const { locationInfo } = useLocationInfo();
  // 获取Context中的供给列表数据
  const { supplyData } = useSupplyData();
  const { selectCityInfo } = locationInfo || {};

  // 服务端渲染时，如果Context中有数据，直接初始化state
  // 这样服务端渲染时就能显示数据，而不是空状态
  const [supplyList, setSupplyList] = useState<EnhancedSupplyCard[]>(() => {
    if (supplyData && supplyData.list.length > 0) {
      return supplyData.list;
    }
    return [];
  });

  // 【isInitFinished】用于控制是否展示骨架屏，初始值为 false 展示骨架屏。
  // - CSR 在第一次请求返回，就会置为 true
  // - SSR 服务端预取数据不为空，则初始值为 true
  const [isInitFinished, setIsInitFinished] = useState<boolean>(() => {
    return Boolean(supplyData && supplyData.list.length > 0);
  });

  // 【isLoading】用于控制展示 Loading 态，包括切换 Tab 的大 Loading，和加载分页数据的小 Loading
  // isLoading = true 时，会根据 supplyList 的长度判断展示哪一个，有供给数据展示小 Loading，无展示大 Loading
  // - CSR 初始值为 true
  // - SSR 初始值根据服务端预取数据决定，有预取数据，则为 false
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    const shouldLoad = !supplyData || supplyData.list.length === 0;
    return shouldLoad;
  });
  const [isLastPage, setIsLastPage] = useState<boolean>(() => {
    // 如果Context中有数据，使用hasMore的相反值
    return supplyData ? !supplyData.hasMore : false;
  });
  const [isRequestError, setIsRequestError] = useState<boolean>(false);
  // 从SSR数据初始化requestId和offset
  const requestOptionsRef = useRef<RequestOptions>({
    requestId: supplyData?.requestId || null,
    offset: supplyData?.list.length || 0,
  });
  // 最新列表请求的 key
  const currentRequestKeyRef = useRef<string | null>(null);
  // 缓存外卖供给列表
  const cacheSupplyListRef = useRef<Map<number, SupplyListCacheItem>>(new Map());

  // 获取外卖列表缓存：key 为 wayneId
  const getCacheSupplyList = useCallback((wayneId: number): GetCacheSupplyListResult => {
    if (!wayneId) return NO_SUPPLY_LIST_CACHE;
    const cacheData = cacheSupplyListRef.current.get(wayneId);
    const isCacheExpired = cacheData && Date.now() - cacheData.timestamp > 10 * 1000;
    // 如果缓存数据不存在或过期，返回空数据
    // 如果缓存列表为空，offset 非真值，返回空数据
    if (
      !cacheData ||
      isCacheExpired ||
      !cacheData.requestId ||
      !cacheData.supplyList?.length ||
      !cacheData.offset
    ) {
      return NO_SUPPLY_LIST_CACHE;
    } else {
      // 否则返回缓存数据
      return {
        isCacheValid: true,
        supplyList: cacheData.supplyList,
        isLastPage: cacheData.isLastPage,
        requestId: cacheData.requestId,
        offset: cacheData.offset,
      };
    }
  }, []);

  // 设置外卖列表缓存
  const setCacheSupplyList = useCallback((wayneId: number, cacheData: SupplyListCacheItem) => {
    if (!wayneId) return;
    cacheSupplyListRef.current.set(wayneId, cacheData);
  }, []);

  // 获取供给列表数据
  const getSupplyListData = useCallback(
    async (isRefresh: boolean = false) => {
      const getSupplyListDataStartTime = Date.now();
      try {
        // 1. 重置列表状态：展示 Loading
        setIsLoading(true);
        setIsRequestError(false);
        // 区分刷新场景 & 加载分页场景
        if (isRefresh) {
          requestOptionsRef.current = {
            requestId: null,
            offset: 0,
          };
          setIsLastPage(false);
          setSupplyList([]);
        }

        // 2. 标识本次请求
        //    请求前拦截：由于需要等待定位，因此在定位回来后判断是否是最新一次请求，不是的话不发起请求，避免浪费资源
        //    响应后拦截：防止用户重新发起请求时使用了旧的响应数据
        
        // 请求券供给使用券对应的韦恩ID
        const wayneId = activeCouponSelection?.value || null;
        const quickId = activeQuickSelection?.value || null;

        const requestKey = `${activeTab?.id}-${activeTab?.type}-${quickId}-${wayneId}-${Date.now()}`;
        currentRequestKeyRef.current = requestKey;

        // 3. 获取通参，顺便等待定位完成
        const commonParams = await getCommonParams();

        // 4. 判断是否最新一次请求，不是的话直接返回，不发起请求
        if (currentRequestKeyRef.current !== requestKey) {
          return;
        }

        // 5. 上报请求供给列表开始时间
        if (!isInitFinished) {
          reportGetSupplyListStart(getSupplyListDataStartTime - (window as any)?.__PAGE_JS_START__);
        }

        // 6. 判断是否可以使用缓存
        //    可用场景：请求外卖 Tab 韦恩供给（listScene = defaultBizScene 或没有 listScene） && 选中了红包快筛（存在 wayneId） && 未选择普通快筛（没有 quickId） && 请求第一页数据（没有 requestId）
        const isRequestWayneSupply = !activeTab?.listScene || activeTab?.listScene === defaultBizScene;
        const isRequestCouponSupply = isRequestWayneSupply && wayneId && !quickId && !requestOptionsRef.current.requestId;
        if (isRequestCouponSupply) {
          const cacheResult = getCacheSupplyList(wayneId);
          if (cacheResult.isCacheValid) {
            // 替换券信息：可能存在多个红包快筛共用一个 wayneId，不同红包的券信息不同
            const enhancedSupplyList = cacheResult.supplyList.map(
              (item) => ({
                  supplyType: SupplyType.Wayne,
                  // 如果缓存数据的下挂券是由券平台召回的最优券，则不使用当前券快筛数据覆盖
                  // 否则需要使用当前券快筛数据覆盖：因为多个券快筛可以复用相同的韦恩 ID，即缓存可以复用，此时展示的下挂券需要使用选中的券快筛
                  poiCard: item.poiCard?.coupon?.couponSource === CouponSource.RECALL
                    ? item.poiCard
                    : addCouponInfo(
                        item.poiCard!,
                        activeCouponSelection?.coupon,
                      ),
                } as EnhancedWayneSupplyCard)
            );
            setSupplyList(enhancedSupplyList);
            setIsLastPage(cacheResult.isLastPage);
            requestOptionsRef.current = {
              requestId: cacheResult.requestId,
              offset: cacheResult.offset,
            };
            return;
          }
        }

        // 7. 构造请求参数
        // 数据片列表：优先取券快筛的数据片，其次取 Tab 的数据片
        //    选中券快筛：使用券对应的 segmentIdList
        //    其它情况：使用 Tab 的 segmentIdList
        const segmentIdList = activeCouponSelection?.coupon?.couponSupply?.segmentIdList
          || activeTab?.segmentIdList || null;
        const params: SupplyListRequestParams = {
          ...commonParams,
          requestId: requestOptionsRef.current.requestId || null,
          tabType: activeTab?.type || null,
          tabId: activeTab?.id || null,
          quickId: quickId || null,
          limit: PAGE_SIZE,
          bizScene: activeTab?.listScene || defaultBizScene,
          wayneId,
          // 数据片 ID 列表
          segmentIdList,
          offset: requestOptionsRef.current.offset || 0,
          latitude: commonParams?.lat || null,
          longitude: commonParams?.lng || null,
          addrLatitude: commonParams?.addrLat || null,
          addrLongitude: commonParams?.addrLng || null,
          // containerVersion 仅团 APP 传 APP 的版本号，团小程序传小程序的版本号，其它传 null
          containerVersion: isRequestWayneSupply ? getMtAppVersion() || getMtWXAppVersion() || null : null,
        };
        // 8. 发起请求
        const { data: result } = await getSupplyList(params);
        const { requestId, supplyCardList = [], isLastPage = false } = result || {};
        // 9. 上报获取列表数据成功率
        if (isRefresh) {
          // 9.1 刷新场景：有列表数据 - 获取成功，没有列表数据 - 获取失败
          reportGetSupplyListSuccessRate(Boolean(supplyCardList?.length), 'init');
        } else {
          // 9.2 加载分页场景：接口成功 - 获取成功，接口失败 - 获取失败
          reportGetSupplyListSuccessRate(true, 'loadMore');
        }

        // 10. 判断是否是最新的请求：如果请求 key 不同，说明用户重新发起了请求，则丢弃之前的响应数据
        if (currentRequestKeyRef.current !== requestKey) {
          return;
        }

        // 11. 格式化列表数据
        const enhancedSupplyList = Array.isArray(supplyCardList)
          ? supplyCardList.map((item: SupplyCard) => {
              return item.supplyType === SupplyType.Wayne
                ? ({
                    supplyType: SupplyType.Wayne,
                    // 如果接口下发了 coupon，则使用接口下发的券信息，否则会使用选中的券快筛的券信息
                    poiCard: item.poiCard?.coupon
                      ? formatPoiInfo(item.poiCard)
                      : activeCouponSelection?.coupon
                        ? addCouponInfo(
                            formatPoiInfo(item.poiCard),
                            activeCouponSelection?.coupon
                          )
                        : formatPoiInfo(item.poiCard),
                  } as EnhancedWayneSupplyCard)
                : ({
                    supplyType: SupplyType.GroupBuy,
                    productCard: formatProductInfo(item.productCard, requestId, `${selectCityInfo?.cityId}`, activeTab?.name || ''),
                  } as EnhancedGroupBuySupplyCard);
            })
          : [];

        // 12. 更新请求参数中的 offset 和 requestId，用于下一页请求
        const nextRequestOffset = requestOptionsRef.current.offset + enhancedSupplyList.length;
        requestOptionsRef.current = {
          offset: nextRequestOffset,
          requestId,
        };

        // 13. 缓存券召回列表
        if (isRequestCouponSupply && requestId) {
          setCacheSupplyList(wayneId, {
            supplyList: enhancedSupplyList,
            isLastPage,
            requestId,
            timestamp: Date.now(),
            offset: nextRequestOffset,
          });
        }

        // 14. 更新供给列表状态
        setIsLastPage(isLastPage);

        const newSupplyList = isRefresh
          ? enhancedSupplyList
          : [...supplyList, ...enhancedSupplyList];
        setSupplyList(newSupplyList);
        setIsLoading(false);

        // 上报获取列表数据成功响应次数
        if (!isInitFinished && newSupplyList.length) {
          reportCustomMetric({
            key: "SupplyListResCount",
            value: 1,
          });
        }
      } catch (error) {
        // 用户未登录，触发登录
        if ((error as any)?.code === 401) {
          try {
            await login();
          } catch (err) {
            reportError({
              name: "[useSupplyList] getSupplyListDataLoginError",
              msg: err,
              level: Level.ERROR,
              category: "jsError",
            });
          }
          return;
        } else {
          reportGetSupplyListSuccessRate(false, isRefresh ? 'init' : 'loadMore');
        }
        setIsLoading(false);
        setIsRequestError(true);
      } finally {
        // 上报供给列表耗时
        if (!isInitFinished) {
          setIsInitFinished(true);
          const getSupplyListDataEndTime = Date.now();
          setModuleLoaded('supplyList');
          reportGetSupplyListEnd(getSupplyListDataEndTime - getSupplyListDataStartTime);
          reportGetSupplyListCost(getSupplyListDataEndTime - (window as any)?.__PAGE_JS_START__);
        }
      }
    },
    [
      supplyList,
      activeTab?.id,
      activeQuickSelection?.id,
      activeCouponSelection?.id,
      selectCityInfo?.cityId,
      isInitFinished,
      login,
      getCacheSupplyList,
      setModuleLoaded,
    ]
  );


  /**
   * 初始化供给列表数据
   * 用户在定位完成之前可能的操作：
   * 1. 第一类操作已经触发了 getSupplyListData(true)，并且页面展示了 Loading 状态，初始数据已经清空，会等待最新的定位，此时可以跳过初始化请求
   *    1.1. 切换其它 Tab
   *    1.2. Tab 未切换，点击普通快筛
   *    1.3. 点击红包快筛、或红包墙点击去使用，选中红包快筛
   *    1.4. 切换到了其它的 Tab，又切换回外卖 Tab
   *    1.5. 未切换 Tab，选中了快筛，又取消筛选快筛
   * 
   * 2. 第二类操作不会触发 getSupplyListData(true)，页面有展示 SSR 数据。需要判断如果定位是否变化，如果定位发生变化，则刷新该 Tab 下的数据，重新回到第一页，不能跳过初始化请求，如果定位没有变化，可以跳过初始化请求
   *    2.1 未切换 Tab、快筛，上拉加载：触发了 getSupplyListData(false)，在已有供给列表下面展示小 Loading
   *    2.2 啥都没做
   */
  const initSupplyList = useCallback(() => {
    // 判断是否需要跳过初始化请求，需要满足以下条件
    // 1. SSR 请求返回了数据：isInitFinished = true
    // 2. 列表数据已经刷新：supplyList.length === 0
    // 3. OR 列表数据未发生刷新，则判断 SSR 定位信息和 CSR 定位信息差距在一定范围内：
    //    3.1 cityId 和当前手选城市 || 真实城市相同
    //    3.2 经纬度差距小于 3 公里
    const isLocationInRange = locationInfo?.ssrDiff?.isSelectLatLngInRange && locationInfo?.ssrDiff?.isSameSelectCityId;
    const isSSRDataExist = Array.isArray(supplyList) && supplyList.length > 0;
    if (isInitFinished) {
      setModuleLoaded('supplyList');
    }
    if (isInitFinished && (!isSSRDataExist || isLocationInRange)) {
      return;
    }
    // 否则触发初始化请求
    getSupplyListData(true);
  }, [
    isInitFinished,
    supplyList,
    locationInfo?.ssrDiff, // 定位信息
  ]);

  // 上拉加载下一页数据
  const loadMoreData = useCallback(async () => {
    // 无下一页 or 正在请求中，不触发加载更多
    if (isLastPage || isLoading) return;
    getSupplyListData(false);
  }, [isLastPage, isLoading, getSupplyListData]);

  return {
    supplyList,
    isInitFinished,
    isLoading,
    isLastPage,
    isRequestError,
    loadMoreData,
    getSupplyListData,
    initSupplyList,
  };
};

export default useSupplyList;
